import React, { createContext, useContext, ReactNode, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthProvider';
import useGetUserInfo, { UserInfo as UserInfoFromHook } from '../hooks/User/useGetUserInfo';
import useGetPowerMenu, { PowerBIMenuItem as PowerBIMenuItemFromHook } from '../hooks/PowerBI/useGetPowerMenu';

type UserInfo = UserInfoFromHook;
type PowerBIMenuItem = PowerBIMenuItemFromHook;

export interface Workspace {
  id: number;
  shortName: string;
  longName: string;
  volumeName: string;
  sender?: string;
  onMaintenance?: number;
}

interface UserContextType {
  userInfos: UserInfo | null;
  loadingUserInfos: boolean;
  hasErrorUserInfos: boolean;
  error: string;
  isUserLoaded: boolean;
  refetchUserInfo: () => void;
  menuItems: PowerBIMenuItem[] | null;
  loadingMenu: boolean;
  hasErrorMenu: boolean;
  menuError: string;
  isMenuLoaded: boolean;
  refetchMenu: () => void;
  workspaces: Workspace[];
  selectedWorkspace: Workspace | null;
  setSelectedWorkspace: (workspace: Workspace | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

const getStorageKey = (username: string): string => `workspace_${username}`;

const compactWorkspaceIdentifier = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const isSameWorkspaceIdentifier = (left: string, right: string): boolean =>
  left === right || compactWorkspaceIdentifier(left) === compactWorkspaceIdentifier(right);

const getWorkspaceVolumeName = (key: string, value: Record<string, unknown>): string => {
  const candidates = [
    value.workspaceName,
    value.volumeName,
    value.volume_name,
    value.workspace,
  ];

  const volumeName = candidates.find((candidate): candidate is string => (
    typeof candidate === 'string' && candidate.trim().length > 0
  ));

  return (volumeName || key).trim();
};

const getStoredWorkspace = (userName: string): string | null => {
  try {
    return localStorage.getItem(getStorageKey(userName));
  } catch (error) {
    console.warn('Error reading workspace from localStorage:', error);
    return null;
  }
};

const setStoredWorkspace = (userName: string, workspaceVolumeName: string): void => {
  try {
    localStorage.setItem(getStorageKey(userName), workspaceVolumeName);
  } catch (error) {
    console.warn('Error saving workspace to localStorage:', error);
  }
};

export const UserProvider = ({ children }: UserProviderProps) => {
  const { isAuthenticated } = useAuth();
  const { data: userInfos, loading: loadingUserInfos, error, refetch: refetchUserInfo } = useGetUserInfo(isAuthenticated);
  const { data: menuItems, loading: loadingMenu, error: menuError, refetch: refetchMenu } = useGetPowerMenu({ 
    shouldFetch: isAuthenticated, 
    workspace: 'PREPROD_LAB',
    returnRaw: true 
  });
  
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);

  const workspaces: Workspace[] = useMemo(() => {
    const workspaceData = userInfos?.availableActifsWorkspace || userInfos?.availableActifsForAllWorkspaces;
    if (!workspaceData) return [];
    
    return Object.entries(workspaceData).map(([key, value], index) => {
      const volumeName = getWorkspaceVolumeName(key, value);

      return {
        id: index + 1,
        shortName: volumeName.toUpperCase(),
        longName: value.longName || key,
        volumeName,
        sender: value.sender,
        onMaintenance: value.onMaintenance || 0
      };
    });
  }, [userInfos?.availableActifsWorkspace, userInfos?.availableActifsForAllWorkspaces]);

  useEffect(() => {
    const userName = userInfos?.username || userInfos?.email || userInfos?.userId;
    if (!userName || workspaces.length === 0) return;
    if (selectedWorkspace) return;

    const storedWorkspaceId = getStoredWorkspace(userName);
    const workspaceToSelect = storedWorkspaceId 
      ? workspaces.find(ws => isSameWorkspaceIdentifier(ws.volumeName, storedWorkspaceId) || ws.id.toString() === storedWorkspaceId) || workspaces[0]
      : workspaces[0];

    if (workspaceToSelect) {
      setSelectedWorkspace(workspaceToSelect);
      setStoredWorkspace(userName, workspaceToSelect.volumeName);
    }
  }, [userInfos?.username, userInfos?.email, userInfos?.userId, workspaces]);

  const handleSetSelectedWorkspace = useCallback((workspace: Workspace | null) => {
    setSelectedWorkspace(workspace);
    const userName = userInfos?.username || userInfos?.email || userInfos?.userId;
    if (workspace && userName) {
      setStoredWorkspace(userName, workspace.volumeName);
    }
  }, [userInfos?.username, userInfos?.email, userInfos?.userId]);

  const contextValue: UserContextType = useMemo(() => ({
    userInfos,
    loadingUserInfos,
    hasErrorUserInfos: !!error,
    error,
    isUserLoaded: !loadingUserInfos && !!userInfos && !error,
    refetchUserInfo,
    menuItems,
    loadingMenu,
    hasErrorMenu: !!menuError,
    menuError,
    isMenuLoaded: !loadingMenu && !!menuItems && !menuError,
    refetchMenu,
    workspaces,
    selectedWorkspace,
    setSelectedWorkspace: handleSetSelectedWorkspace,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [userInfos, loadingUserInfos, error, menuItems, loadingMenu, menuError, workspaces, selectedWorkspace, handleSetSelectedWorkspace]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
